/**
 * Gift subscriptions: buying a year of Grow More for someone (no account
 * needed), the thank-you page's gift, reading a gift code before signing in,
 * and redeeming one.
 *
 * Nothing about a card comes near this code: the payment is Stripe
 * Checkout's own page, and all this does is ask the backend for its URL.
 *
 * Endpoints (every answer also carries `success`):
 * - GET  /gifts/shop → GiftShop (no sign-in).
 * - POST /gifts/checkout GiftOrder → { url } (no sign-in).
 * - GET  /gifts/purchased?session_id= → { status: 'ready', gift } or
 *   { status: 'pending' } while Stripe's payment goes through.
 * - GET  /gifts/code?code= → GiftCodeState (no sign-in).
 * - POST /gifts/redeem { code } → RedeemedGift (signed in); refused with a
 *   GIFT_* code (giftRefusalOf).
 *
 * Who uses it: features/gift (GiftPage, GiftThanksPage, GiftRedeemPage,
 * GiftClaim, GiftCodeBox). Nothing is cached under a shared key; a redeem
 * invalidates BILLING_QUERY_KEY, since the gardener's access changed.
 *
 * @module gifts
 */

import { apiClient } from './apiClient';
import type { Access, Price } from './billing';

/** Whether gifts can be bought now. */
export interface GiftShop {
  /** open: on sale; soon: before the on-sale day; unavailable: on sale, but not set up here yet. */
  state: 'open' | 'soon' | 'unavailable';
  /** The first day gifts are on sale, YYYY-MM-DD. */
  opensOn: string;
  /** What a gift costs now; null when not open. */
  price: Price | null;
  /** How many days ahead the gift email may be set to go. */
  sendMaxDays: number;
  /** The buyer's hour a gift email goes on a chosen day (the Grow setting gift.SEND_LOCAL_HOUR). */
  sendHour: number;
}

/** A gift order, as the gift page sends it. */
export interface GiftOrder {
  /** The buyer's name, for the gift email ("Amy gave you…"). */
  buyerName: string;
  /** The buyer's email: the receipt and the code go there. */
  buyerEmail: string;
  /** The receipt's language: the page's. */
  buyerLanguage: 'en' | 'es';
  /** The recipient's name; optional. */
  recipientName?: string;
  /** The recipient's email; empty when the buyer gives the code themselves. */
  recipientEmail?: string;
  /** The gift email's language. */
  language: 'en' | 'es';
  /** A short message for the gift email; optional. */
  message?: string;
  /** The day chosen, YYYY-MM-DD; absent for today. */
  sendOn?: string;
  /**
   * The buyer's time zone (their browser's IANA name, such as "America/Chicago"), with a chosen day: the
   * server works out the moment the gift email goes, the Grow setting's hour on that day in this zone.
   */
  timeZone?: string;
}

/** A bought gift, as the thank-you page shows it. */
export interface PurchasedGift {
  /** GROW-XXXX-XXXX. */
  code: string;
  /** The link that redeems it. */
  redeemUrl: string;
  /** For whom. */
  recipientName: string | null;
  /** Null when the buyer gives the code themselves. */
  recipientEmail: string | null;
  /** The day the gift email goes, YYYY-MM-DD. */
  sendOn: string | null;
  /** The moment it goes, ISO. */
  sendAt: string | null;
  /** Whether the email goes later (the thank-you page names its day) rather than now, by the server's clock. */
  scheduled: boolean;
  /** Paid, in cents. */
  amount: number;
  /** Stripe's lowercase code. */
  currency: string;
}

/** What a gift code is, read before signing in. */
export interface GiftCodeState {
  /** ready: unused; used; void: switched off; unknown. */
  status: 'ready' | 'used' | 'void' | 'unknown';
  /** The code as stored, or null when it cannot be one. */
  code: string | null;
  /** Who gave it (only while unused). */
  from: string | null;
  /** For whom (only while unused). */
  to: string | null;
  /** The giver's message (only while unused). */
  message: string | null;
}

/** What redeeming a code answered. */
export interface RedeemedGift {
  /** trial: Grow More until endsAt; subscription: a year added to their subscription. */
  how: 'trial' | 'subscription';
  /** When the gift year ends, ISO. */
  endsAt: string;
  /** Their access now. */
  access: Access;
}

/** Why redeeming was refused, as the server says it. */
export type GiftRefusal = 'GIFT_UNKNOWN' | 'GIFT_USED' | 'GIFT_VOID' | 'GIFT_UNAVAILABLE' | 'GIFT_TOO_FAR' | 'GIFT_FAILED';

/**
 * Whether gifts can be bought, and the price.
 *
 * @returns The shop's state.
 */
export async function getGiftShop(): Promise<GiftShop> {
  const response = await apiClient.get<GiftShop & { success: boolean }>('/gifts/shop');
  return response.data;
}

/**
 * Stripe Checkout for a gift. Resolves with the page to go to.
 *
 * @param order - The form.
 * @returns Stripe's checkout address.
 */
export async function startGiftCheckout(order: GiftOrder): Promise<string> {
  const response = await apiClient.post<{ success: boolean; url: string }>('/gifts/checkout', order);
  return response.data.url;
}

/**
 * The gift Stripe's Checkout came back with: ready (its code), or pending while the payment goes through.
 *
 * @param sessionId - From the thank-you page's address.
 * @returns The gift, or pending.
 */
export async function getPurchasedGift(sessionId: string): Promise<{ status: 'ready'; gift: PurchasedGift } | { status: 'pending' }> {
  const response = await apiClient.get<{ success: boolean; status: 'ready' | 'pending'; gift?: PurchasedGift }>(
    '/gifts/purchased', { params: { session_id: sessionId } },
  );
  return response.data.status === 'ready' && response.data.gift
    ? { status: 'ready', gift: response.data.gift }
    : { status: 'pending' };
}

/**
 * What a gift code is. Needs no sign-in.
 *
 * @param code - As typed or from the link.
 * @returns Its state.
 */
export async function readGiftCode(code: string): Promise<GiftCodeState> {
  const response = await apiClient.get<GiftCodeState & { success: boolean }>('/gifts/code', { params: { code } });
  return response.data;
}

/**
 * Redeems a gift code for the signed-in gardener. Rejects with the server's
 * answer (response.data.code: a GiftRefusal; mine: true when they used it themselves).
 *
 * @param code - As typed or from the link.
 * @returns What it did, and their access now.
 */
export async function redeemGift(code: string): Promise<RedeemedGift> {
  const response = await apiClient.post<RedeemedGift & { success: boolean }>('/gifts/redeem', { code });
  return response.data;
}

/**
 * The refusal in a failed redeem, when the server gave one.
 *
 * @param error - What redeemGift rejected with.
 * @returns The code and whether it was their own use, or null for a failure without one (offline, a crash).
 */
export function giftRefusalOf(error: unknown): { code: GiftRefusal; mine: boolean } | null {
  const data = (error as { response?: { data?: { code?: unknown; mine?: unknown } } })?.response?.data;
  return typeof data?.code === 'string' && data.code.startsWith('GIFT_')
    ? { code: data.code as GiftRefusal, mine: data.mine === true }
    : null;
}
